import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, HTMLAttributes, RefObject } from 'react';
import React, { forwardRef } from 'react';
import { plainButton } from '../../styles/utilities.css';
import { Flex } from '../Flex/Flex';
import { Icon } from '../Icon/Icon';
import type { IconName } from '../Icon/Icon';
import type { IconButtonVariants } from './IconButton.css';
import * as styles from './IconButton.css';

export type IconButtonProps<TElementType extends 'button' | 'div' = 'button'> = IconButtonVariants &
  (TElementType extends 'button' ? ButtonHTMLAttributes<HTMLButtonElement> : HTMLAttributes<HTMLDivElement>) & {
    readonly icon: IconName;
    readonly as?: TElementType;
    readonly disabled?: boolean;
  };

export const IconButton = forwardRef<HTMLButtonElement | HTMLDivElement, IconButtonProps<'button' | 'div'>>(
  ({ icon, className: externalClassName, size = 'md', disabled = false, as = 'button', background, ...props }, ref) => {
    const elementTypeProps = as === 'button' ? { disabled, type: 'button' } : {};

    return (
      <Flex
        alignItems="center"
        as={as}
        className={clsx(plainButton, styles.iconButton({ size, background }), externalClassName)}
        justifyContent="center"
        ref={ref}
        {...elementTypeProps}
        {...props}
      >
        {<Icon name={icon} size={size === 'sm' ? 'sm' : 'md'} variant={disabled ? 'disabled' : 'secondary'} />}
      </Flex>
    );
  },
) as <TElementType extends 'button' | 'div' = 'button'>(
  props: IconButtonProps<TElementType> & {
    readonly ref?: TElementType extends 'button' ? RefObject<HTMLButtonElement> : RefObject<HTMLDivElement>;
  },
) => JSX.Element;
